import React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Search, Loader2, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DataTable } from '@/components/ui/data-table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { EnhancementProps } from './index';
import {
  usePreviewDriveSheets,
  type PreviewMatchedSheet,
} from '@/hooks/useConnectors';
import { useConnectorTypeSchema } from '@/hooks/useConnectorTypes';
import { splitFormData } from '@/lib/validation/splitFormData';

const matchedColumns: ColumnDef<PreviewMatchedSheet>[] = [
  {
    accessorKey: 'name',
    header: 'Sheet',
    cell: ({ row }) =>
      row.original.web_view_link ? (
        <a
          href={row.original.web_view_link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-2 hover:no-underline"
        >
          {row.original.name}
        </a>
      ) : (
        row.original.name
      ),
  },
  { accessorKey: 'parent_folder_name', header: 'Parent folder' },
  { accessorKey: 'modified_time', header: 'Modified' },
  { accessorKey: 'owner_email', header: 'Owner' },
];

export function PreviewMatchingSheetsButton({
  connectorType,
  formValues,
  connectorName,
  // onFieldChange is intentionally unused: read-only preview, never writes back.
}: EnhancementProps) {
  const { data: schema } = useConnectorTypeSchema(connectorType);
  const preview = usePreviewDriveSheets();
  const [open, setOpen] = React.useState(false);

  const folderId = formValues['drive_folder_id'];
  const disabled =
    typeof folderId !== 'string' || folderId.trim() === '' || preview.isPending;

  const handlePreview = () => {
    const fields = schema?.fields ?? [];
    const { credentials, settings } = splitFormData(formValues, fields);
    if (connectorName) {
      preview.mutate({ connector_name: connectorName, settings });
    } else {
      preview.mutate({ credentials, settings });
    }
  };

  const data = preview.data;

  return (
    <div className="mt-3 space-y-3">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={handlePreview}
      >
        {preview.isPending ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Search className="mr-2 h-4 w-4" />
        )}
        Preview matching sheets
      </Button>
      {!folderId && (
        <p className="text-xs text-muted-foreground">
          Enter a Drive folder ID to preview which sheets the criteria match.
        </p>
      )}

      {preview.error && (
        <Alert variant="destructive">
          <AlertDescription>
            {(preview.error as Error).message || 'Preview failed.'}
          </AlertDescription>
        </Alert>
      )}

      {data && (
        <div className="rounded-lg border bg-card shadow-sm">
          <div className="flex flex-wrap items-center gap-2 border-b p-3 text-sm">
            <Badge variant="default">Matched ({data.matched_total})</Badge>
            <Badge variant="secondary">Skipped ({data.skipped_total})</Badge>
            <span className="text-muted-foreground">
              Scanned {data.scanned} folders · depth {data.max_depth}
            </span>
            {data.truncated && (
              <Badge variant="outline">Truncated — showing first results</Badge>
            )}
          </div>

          {(data.unattributed_children ?? 0) > 0 && (
            <Alert className="rounded-none border-x-0 border-t-0">
              <AlertDescription>
                {data.unattributed_children} item(s) in this folder could not be
                attributed and are not shown — the list below may be incomplete.
              </AlertDescription>
            </Alert>
          )}

          <div className="p-3">
            {data.matched.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No sheets matched these criteria.
              </p>
            ) : (
              <DataTable columns={matchedColumns} data={data.matched} />
            )}
          </div>

          {data.skipped_total > 0 && (
            <Collapsible open={open} onOpenChange={setOpen} className="border-t">
              <CollapsibleTrigger className="flex w-full items-center gap-1 p-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                <ChevronRight
                  className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`}
                />
                Skipped ({data.skipped_total})
              </CollapsibleTrigger>
              <CollapsibleContent className="px-3 pb-3">
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {data.skipped_sample.map((s) => (
                    <li key={`${s.name}-${s.parent_folder_name}`}>
                      <span className="font-medium text-foreground">{s.name}</span>{' '}
                      — {s.reason}
                    </li>
                  ))}
                  {data.skipped_total > data.skipped_sample.length && (
                    <li>…and {data.skipped_total - data.skipped_sample.length} more</li>
                  )}
                </ul>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      )}
    </div>
  );
}
